import { NewMessageFormData } from '@src/main/modules/messages/ui/mailbox/components/new-message/types/NewMessageFormData';
import { customToolbar } from '@src/main/shared/ui/components/input/customToolbar';
import DOMPurify from 'dompurify';
import { Editor } from 'primereact/editor';
import { FC } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  control: Control<NewMessageFormData>;
  setPlainText: (value: string) => void;
  styles: Record<string, string>;
}

export const MessageEditor: FC<Props> = ({ control, setPlainText, styles }) => {
  const { t } = useTranslation();

  return (
    <div className={`${styles['form-section']} ${styles['textarea-section']}`}>
      <label>{t('messages.message')}</label>
      <Controller
        name="messageHtml"
        control={control}
        rules={{ required: t('messages.validations.required') }}
        render={({ field, fieldState }) => (
          <div className={styles['form-section']}>
            <Editor
              value={field.value}
              onTextChange={(e) => {
                const htmlValue = e.htmlValue ?? '';
                let olIndex = 1;
                const sanitizedHtml = DOMPurify.sanitize(htmlValue, {
                  ALLOWED_TAGS: ['br', 'p', 'ul', 'ol', 'li'],
                  ALLOWED_ATTR: ['data-list'],
                });
                const textValue = sanitizedHtml
                  .replace(/<p[^>]*>/gi, '')
                  .replace(/<\/p>\s*<br\s*\/?>/gi, '\n')
                  .replace(/<\/p>/gi, '\n')
                  .replace(/<br\s*\/?>/gi, '\n')
                  .replace(/\r\n/g, '\n')
                  .replace(/\n{3,}/g, '\n')
                  .replace(/<ul[^>]*>/gi, '')
                  .replace(/<\/ul>/gi, '')
                  .replace(/<ol[^>]*>/gi, () => {
                    olIndex = 1;
                    return '';
                  })
                  .replace(/<\/ol>/gi, '')

                  .replace(/<li([^>]*)>/gi, (attrs) => {
                    const isOrdered = /data-list="ordered"/i.test(attrs);
                    const isBullet = /data-list="bullet"/i.test(attrs);

                    if (isOrdered) {
                      const number = olIndex;
                      olIndex += 1;
                      return `${number}. `;
                    }

                    if (isBullet) return '• ';
                    return '';
                  })
                  .replace(/<\/li>/gi, '\n')
                  .trim();
                field.onChange(sanitizedHtml);
                setPlainText(textValue);
              }}
              style={{ height: '320px' }}
              placeholder={t('common.editorPlaceholder')}
              className={fieldState.error ? 'p-invalid' : ''}
              headerTemplate={customToolbar}
            />
            {fieldState.error && <small className="p-error">{fieldState.error.message}</small>}
          </div>
        )}
      />
    </div>
  );
};
